// betreffKontaktKopf.ts — führenden Kundennamen aus einem Mail-Betreff lösen.
//
// T-46 (30.09.2026): Bei „Neuer Vorgang" aus einer Mail bot die Betreff-Pille
// z. B. „Moll-Rubienke, Vanessa / Absprachen wegen Termin" an. Der Kundenname
// steht im Vorgang ohnehin als Zuweisung — im Betreff ist er doppelt.
//
// Fix 01.10.2026 (PO-Regel aus Runde 3: „beim Befüllen dann (Kundenname bis
// zum ersten /) rausnehmen"): T-46 schnitt nur, wenn der Teil vor dem „/" dem
// Namen eines zugewiesenen Kontakts entsprach. Bei „Neuer Vorgang" aus der
// Mail ist aber oft noch KEIN Kontakt gewählt — dann blieb „Kundenname/ …"
// stehen. Seitdem gilt: Der Teil bis einschließlich zum ERSTEN „/" fällt
// IMMER weg. Einzige Ausnahme ist das Courté-Schema („Acme GmbH - VSNR 4711 /
// …", E04): Steht „ - VSNR " vor dem ersten „/", greift unverändert die
// T-46-Regel (Kopf nur bei Namensgleichheit weg, „VSNR …" bleibt stehen).
//
// Liegt unter `utils/` (wie `betreffPraefix.ts`), damit `components/vorgang/**`
// nicht in `components/mail/**` hineingreift — und ohne Abhängigkeit auf
// `components/`, deshalb nimmt `kontaktNamensVarianten` ein schlichtes Objekt.

import { trenneAntwortPraefix } from "./betreffPraefix";

/** Die Namensbestandteile eines Kontakts, wie `KontaktRef` sie trägt. */
export interface KontaktName {
    lastname?: string | null;
    firstName?: string | null;
}

/**
 * Alle Schreibweisen, unter denen ein Kontakt vorn im Betreff stehen kann:
 * „Nachname, Vorname" (wie `kontaktLabel`), „Vorname Nachname" und der
 * Nachname allein (Firmen). Steht der volle Anzeigename schon in `lastname`
 * („Moll-Rubienke, Vanessa"), kommt auch dessen umgedrehte Form dazu.
 * Der Platzhalter „Kontakt <id>" ist kein Name und fällt weg.
 */
export function kontaktNamensVarianten(k: KontaktName | null | undefined): string[] {
    if (!k) return [];
    const nach = (k.lastname ?? "").trim();
    const vor = (k.firstName ?? "").trim();
    if (!nach && !vor) return [];
    if (/^Kontakt \d+$/.test(nach) && !vor) return [];
    const varianten = new Set<string>();
    const add = (s: string) => {
        const t = s.trim();
        if (t) varianten.add(t);
    };
    add([nach, vor].filter(Boolean).join(", "));
    add([vor, nach].filter(Boolean).join(" "));
    add(nach);
    if (!vor) {
        // Anzeigename „Nachname, Vorname" steckt komplett im lastname.
        const teile = nach.split(",").map((t) => t.trim());
        if (teile.length === 2 && teile[0] && teile[1]) add(`${teile[1]} ${teile[0]}`);
    }
    return [...varianten];
}

const normal = (s: string) => s.toLocaleLowerCase("de").replace(/\s+/g, " ").trim();

const VSNR_TRENNER = / - VSNR /i;

/**
 * Entfernt den Kopf eines Mail-Betreffs für die Übernahme in den Vorgang:
 * „Kundenname/ Kunde Betreff" → „Kunde Betreff",
 * „AW: Moll-Rubienke, Vanessa / Absprachen" → „AW: Absprachen".
 *
 * Regel: Der Text bis einschließlich zum ERSTEN „/" fällt immer weg — egal,
 * ob ein Kontakt gewählt ist oder der Kopf einem Namen entspricht. Danach wird
 * getrimmt. Kein „/" → Betreff unverändert. Bleibt nach dem Schnitt nichts
 * übrig („X/"), kommt der Originalbetreff zurück.
 *
 * Courté-Schema (T-46, unverändert): Steht „ - VSNR " VOR dem ersten „/",
 * wird nur der Teil vor „ - VSNR " abgeschnitten, und nur wenn er einem der
 * `namen` entspricht (Groß-/Kleinschreibung und Leerzeichen egal); „VSNR …"
 * bleibt in jedem Fall stehen.
 *
 * Ein Antwort-Präfix („AW:", „WG:" …) bleibt vorn; das Abschneiden erledigt
 * der Aufrufer wie bisher (`betreffOhneAntwortPraefix`).
 */
export function betreffOhneKontaktKopf(
    subject: string | null | undefined,
    namen: readonly string[] = [],
): string {
    const s = (subject ?? "").trim();
    if (!s) return "";

    const { praefix, rest } = trenneAntwortPraefix(s);
    const slash = rest.indexOf("/");
    const vsnr = VSNR_TRENNER.exec(rest);
    const vsnrPos = vsnr ? vsnr.index : -1;

    if (vsnrPos >= 0 && (slash < 0 || vsnrPos < slash)) {
        const ziel = new Set(namen.map(normal).filter(Boolean));
        if (!ziel.has(normal(rest.slice(0, vsnrPos)))) return s;
        const neu = rest.slice(vsnrPos + " - ".length).trim(); // „VSNR …" bleibt stehen
        return neu ? `${praefix}${neu}` : s;
    }
    if (slash < 0) return s;

    const neu = rest.slice(slash + 1).trim();
    return neu ? `${praefix}${neu}` : s;
}
